import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getCurrentUser } from "../services/auth";
import { postUserAuditEvent } from "../services/userAuditLog";

const SESSION_KEY = "zunoAuditSessionId";
const NAV_CLICK_KEY = "zunoAuditLastNavigationClick";
const MAX_LABEL_LENGTH = 180;

function getAuditSessionId() {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const next =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function cleanText(value, maxLength = MAX_LABEL_LENGTH) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);
}

function getElementLabel(element) {
  if (!element) return "";
  return cleanText(
    element.getAttribute("aria-label") ||
      element.getAttribute("title") ||
      element.getAttribute("name") ||
      element.value ||
      element.textContent ||
      element.dataset?.auditLabel ||
      ""
  );
}

function getElementRole(element) {
  if (!element) return "";
  const explicitRole = element.getAttribute("role");
  if (explicitRole) return explicitRole;
  return String(element.tagName || "").toLowerCase();
}

function getTargetPath(element) {
  if (!element) return "";
  const href = element.getAttribute("href") || element.dataset?.href || "";
  if (!href) return "";
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return url.href;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

function serializeEvent(event) {
  const element = event.target?.closest?.(
    "a, button, [role='button'], input[type='button'], input[type='submit']"
  );
  if (!element) return null;

  const label = getElementLabel(element);
  const toPath = getTargetPath(element);
  const fromPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  return {
    eventType: "ui_click",
    eventAction: element.matches("a") ? "link_click" : "button_click",
    pagePath: fromPath,
    pageTitle: document.title,
    fromPath,
    toPath,
    elementRole: getElementRole(element),
    elementLabel: label || "Unlabelled action",
    metadata: {
      button: event.button,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
      elementId: element.id || "",
      elementClasses: cleanText(element.className || "", 240),
    },
  };
}

export default function UserAuditTracker() {
  const location = useLocation();
  const previousPathRef = useRef("");
  const lastPageViewRef = useRef("");
  const sessionId = useMemo(getAuditSessionId, []);

  useEffect(() => {
    const handleClick = (event) => {
      if (!getCurrentUser()) return;
      const payload = serializeEvent(event);
      if (!payload) return;

      const enriched = { ...payload, sessionId };
      if (payload.toPath) {
        try {
          window.sessionStorage.setItem(
            NAV_CLICK_KEY,
            JSON.stringify({
              at: Date.now(),
              fromPath: payload.fromPath,
              toPath: payload.toPath,
              elementLabel: payload.elementLabel,
              elementRole: payload.elementRole,
            })
          );
        } catch {
          // Navigation context is best effort; the click itself is still recorded.
        }
      }

      postUserAuditEvent(enriched).catch(() => {});
    };

    const handleSubmit = (event) => {
      if (!getCurrentUser()) return;
      const form = event.target;
      const fromPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      postUserAuditEvent({
        eventType: "ui_action",
        eventAction: "form_submit",
        pagePath: fromPath,
        pageTitle: document.title,
        fromPath,
        toPath: form?.getAttribute?.("action") || "",
        elementRole: "form",
        elementLabel: getElementLabel(form) || cleanText(form?.id || form?.className || "Form submit"),
        metadata: { formId: form?.id || "" },
        sessionId,
      }).catch(() => {});
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("submit", handleSubmit, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("submit", handleSubmit, true);
    };
  }, [sessionId]);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user) return;

    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (lastPageViewRef.current === currentPath) return;

    let navigationClick = null;
    try {
      const stored = window.sessionStorage.getItem(NAV_CLICK_KEY);
      navigationClick = stored ? JSON.parse(stored) : null;
      if (navigationClick && Date.now() - Number(navigationClick.at || 0) > 5000) {
        navigationClick = null;
      }
      window.sessionStorage.removeItem(NAV_CLICK_KEY);
    } catch {
      navigationClick = null;
    }

    const hadPreviousPath = Boolean(previousPathRef.current);
    const fromPath = navigationClick?.fromPath || previousPathRef.current || document.referrer || "";
    lastPageViewRef.current = currentPath;
    previousPathRef.current = currentPath;

    postUserAuditEvent({
      eventType: "page_view",
      eventAction: "view_page",
      pagePath: currentPath,
      pageTitle: document.title,
      fromPath,
      toPath: currentPath,
      elementRole: navigationClick?.elementRole || "",
      elementLabel: navigationClick?.elementLabel || "",
      metadata: {
        navigationSource: navigationClick ? "tracked_click" : hadPreviousPath ? "route_change" : "direct",
      },
      sessionId,
    }).catch(() => {});
  }, [location, sessionId]);

  return null;
}
